import { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FormField, FormHelp, Label, Select } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyMultiEditWebApiHandler_multi } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { WizardContext } from '../../../../components/Wizard/context';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { AppSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { useForm } from '../../../../hooks';

import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../../components/Wizard/actions';
import PropertiesActions from '../../../../actions/PropertiesActions';

import typeCheckUtils from '../../../../utils/typeCheckUtils';
import { multiFieldsUtil, SAVE_TO_PARENT_KEY } from '../../../../utils/postingUtils';
import { postingConstraints } from '../../../../utils/formValidation';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import { bathOptions } from '../../../../constants/ListingDetailsConstants';

import WizardServerError from '../../../../model/WizardServerError';
import FloorPlan from '../../../../model/FloorPlan';

import type { ChangeEvent } from 'react';
import type { MockEventProps } from '../types';

const inputName = 'baths';
const multiEndpointFieldName = 'numBathrooms';

const BathroomsFormField = () => {
  const dispatch = useDispatch();
  const { mutateAsync: saveMultipleFieldsPropertyInfo } = useRentalManagerUserPropertyMultiEditWebApiHandler_multi();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { serverErrorObj, isSaveComplete, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { floorplans, listingAlias } = useSelector((state) => state.properties.currentProperty);
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const floorplan = floorplans[0] || {};
  const { baths } = floorplan;

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      baths,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: 'baths',
          // @ts-expect-error
          value: inputs.baths,
        },
      });
    }
  }, [isSaveComplete]);

  let bathOptionsValues = bathOptions;

  if ((baths ?? '') !== '') {
    bathOptionsValues = bathOptions.slice(1);
  }

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event: ChangeEvent<HTMLSelectElement> | MockEventProps) => {
    const { value, name: fieldName } = event.target;

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    if (hasFieldError(fieldName, value)) {
      return;
    }

    const numCastValue = typeCheckUtils.Number(value);

    const dataToSave = multiFieldsUtil.getDataToSave({
      fieldName: multiEndpointFieldName,
      floorPlanId: floorplan?.floorPlanId,
      listingAlias,
      saveToParentKey: SAVE_TO_PARENT_KEY.FLOOR_PLANS,
      value: numCastValue,
    });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    saveMultipleFieldsPropertyInfo({ postData: dataToSave })
      .then((response) => {
        const errorMessage = multiFieldsUtil.getMultiEditErrorMessageFromResponse(response);
        if (errorMessage) {
          updateServerError(errorMessage, fieldName);
          return;
        }
        const { listingOutputFieldData } = response;
        // all data properties are not returned in the response
        const mergedFloorPlan = {
          ...floorplans[0],
          ...listingOutputFieldData?.floorPlans[0],
        };

        // TODO: BE needs to sync responses on various endpoints beds, numBedrooms
        // sync mismatched keys from response
        const floorPlan = FloorPlan(mergedFloorPlan);
        floorPlan.beds = mergedFloorPlan?.numBedrooms ?? '';
        floorPlan.baths = mergedFloorPlan?.numBathrooms ?? '';

        // if squareFeet is null the key is not returned in the response
        floorPlan['squareFeet'] = String(listingOutputFieldData?.floorPlans[0]?.squareFeet ?? '');

        // make sure we pass an array
        dispatch(PropertiesActions.updateCurrentProperty({ floorplans: [floorPlan] }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: unknown) => {
        updateServerError((err as { message?: string })?.message ?? '', fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        // this will always display at the page level
        // the field level server errors should be handle in review page modal
        displayAt: DISPLAY_SERVER_ERROR_AT.PAGE,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: ChangeEvent) => {
    clearServerError();
    // @ts-expect-error
    handleFormChange(event);
  };

  return (
    <FormField
      data-testid="BathroomsFormField"
      label={<Label required={true}>Total bathrooms</Label>}
      control={
        <Select
          size={isScreenDesktop ? 'lg' : 'md'}
          aria-label="Total bathrooms"
          name={inputName}
          value={String(inputs.baths ?? '')}
          onChange={handleChange}
          onBlur={save}
          // @ts-expect-error - error prop is not yet typed in v10
          error={!!errorObj.baths}
          required={true}
          css={{ whiteSpace: 'pre' }}
        >
          {bathOptionsValues.map((option) => {
            if (option === '10+') {
              return (
                <option key={option} value={10}>
                  {option}
                </option>
              );
            }
            return (
              <option key={option} value={option}>
                {option}
              </option>
            );
          })}
        </Select>
      }
      // @ts-expect-error
      description={<FormHelp error={errorObj.baths}>{errorObj.baths}</FormHelp>}
    />
  );
};

export default BathroomsFormField;
